import React from 'react'
// ⚠️ Import from the BARE `@mui/material` barrel, never the deep `@mui/material/SvgIcon`
// path. `apps/platform/vite.config.ts` dedupes on bare specifiers, so a deep path is
// optimized into a SEPARATE dep chunk with its own React — which crashes the app with
// "Invalid hook call" the moment the icon renders. It is not a type error and not a build
// failure; it shows up only at runtime, as a full-page error dialog. Diagnosed here by two
// different `?v=` hashes in the same React stack (`SvgIcon2` from one chunk, `Checkbox2`
// from another). Same trap applies to any other MUI import added to this package.
import { SvgIcon } from '@mui/material'

/**
 * The UNCHECKED checkbox box — a 1px stroked rounded square.
 *
 * VRG-474. MUI's default `CheckBoxOutlineBlank` is not a stroked outline at all: it is a
 * FILLED path forming a ring 2 viewBox units thick (outer box 3..21, inner hole 5..19).
 * At the default 24px icon size that is a **2px** border — twice what every other control
 * in the app draws — and `Table.tsx` then painted a `stroke` on the same path on top of the
 * fill, which straddles both contours and pushed the rendered thickness to **3px**
 * (2.5..5.5 in viewBox units). Measured in the browser on the Certificates list, where a
 * column of them is what made the weight obvious.
 *
 * This draws the same 18x18 outer footprint with a real 1px stroke instead, so a checkbox
 * agrees with the 1px border every field, card and table outline in the app already uses
 * (`fieldChrome.fieldBorderColor`). Corner radius 2.5 on the centre line = 3 on the outer
 * edge, within a pixel of MUI's CHECKED icon (radius 2) so the shape does not jump on
 * toggle.
 *
 * `fill="none"` matters: it is what makes the per-site `svg path { fill }` overrides that
 * used to mute this icon inapplicable, which is deliberate — the colour is now owned by the
 * theme (`theme.ts`'s `MuiCheckbox`), one place, for both apps.
 */
export const CheckBoxBlankIcon = (props: React.ComponentProps<typeof SvgIcon>) => (
  <SvgIcon {...props}>
    <rect
      x="3.5"
      y="3.5"
      width="17"
      height="17"
      rx="2.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
    />
  </SvgIcon>
)

/**
 * The same icon as a ready-made ELEMENT.
 *
 * `theme.ts` is a `.ts` file and cannot hold JSX, and renaming it to `.tsx` would touch
 * every `./theme` importer for no benefit. So the element is built here and the theme
 * consumes it as a value.
 */
export const checkBoxBlankIcon = <CheckBoxBlankIcon />

